import { ApolloError } from "@apollo/client";
import { triggerNotification } from "@store/slices/notification";
import { Dispatch } from "redux";

/**
 * Hilfsfunktion zum Behandeln von Fehlern mit Benachrichtigungen
 * @param error - Der aufgetretene Fehler
 * @param dispatch - Redux Dispatch-Funktion
 * @param customTitle - Optionaler benutzerdefinierter Titel für die Benachrichtigung
 * @param customMessage - Optionale benutzerdefinierte Nachricht für die Benachrichtigung
 */
export const handleErrorWithNotification = (
    error: Error | ApolloError,
    dispatch: Dispatch,
    customTitle?: string,
    customMessage?: string
) => {
    // Fehler in der Konsole loggen
    console.error(error);
    
    // Fehlermeldung extrahieren
    let errorMessage = customMessage;
    if (!errorMessage) {
        if (error instanceof ApolloError) {
            // GraphQL-Fehler behandeln
            errorMessage = mapGraphQLErrorToFriendlyMessage(error);
        } else {
            // Standardfehler behandeln
            errorMessage = error.message || "Ein unbekannter Fehler ist aufgetreten";
        }
    }
    
    // Fehlerbenachrichtigung anzeigen
    dispatch(triggerNotification({
        title: customTitle || "Fehler",
        content: errorMessage,
        type: "error",
        autoHideDuration: 5000
    }));
};

/**
 * Versucht, häufige GraphQL-Fehler auf benutzerfreundliche Meldungen abzubilden.
 * Fällt zurück auf die Originalmeldung, wenn kein Mapping greift.
 */
export const mapGraphQLErrorToFriendlyMessage = (error: ApolloError | string): string => {
    const raw = typeof error === 'string' ? error : (error.message || '').toLowerCase();

    // Preis-/Checkout-spezifische Fälle
    if (raw.includes('monthly price mapping') || raw.includes('price not configured') || raw.includes('cannot read properties of undefined') && raw.includes('price_')) {
        return 'Preiskonfiguration wird aktualisiert. Bitte versuchen Sie es in Kürze erneut oder kontaktieren Sie den Support.';
    }

    // Spezielle Behandlung für neue Produktentfernungs-Fälle
    if (raw.includes('invalid_products_all_removed')) {
        return 'Alle ausgewählten Produkte sind nicht mehr verfügbar. Die Produktliste wird aktualisiert.';
    }

    if (raw.includes('products_removed_from_cart')) {
        return 'Einige veraltete Produkte wurden automatisch aus dem Warenkorb entfernt. Sie können mit den verbleibenden Produkten fortfahren.';
    }

    if (raw.includes('unauthenticated') || raw.includes('access denied')) {
        return 'Your session has expired. You will be redirected to login automatically.';
    }

    // Checkout/Payment specific errors
    if (raw.includes('stripe') && (raw.includes('price') || raw.includes('checkout'))) {
        return 'Payment system temporarily unavailable. Please try again in a moment or contact support.';
    }
    
    if (raw.includes('subscription') && (raw.includes('expired') || raw.includes('cancelled'))) {
        return 'Your subscription status has changed. Please refresh the page and try again.';
    }

    if (raw.includes('bad_request') || raw.includes('graphql operations must contain')) {
        return 'Unerwartete Anfrage. Bitte aktualisieren Sie die Seite und versuchen Sie es erneut.';
    }

    // Fallback
    return typeof error === 'string' ? error : (error.message || 'Ein unbekannter Fehler ist aufgetreten');
};

/**
 * Hilfsfunktion zum Anzeigen von Erfolgsbenachrichtigungen
 * @param message - Die anzuzeigende Erfolgsmeldung
 * @param dispatch - Redux Dispatch-Funktion
 * @param title - Optionaler Titel für die Benachrichtigung
 */
export const showSuccessNotification = (
    message: string,
    dispatch: Dispatch,
    title?: string
) => {
    dispatch(triggerNotification({
        title: title || "Erfolg",
        content: message,
        type: "success",
        autoHideDuration: 3000
    }));
};

/**
 * Hilfsfunktion zum Anzeigen von Informationsbenachrichtigungen
 * @param message - Die anzuzeigende Informationsmeldung
 * @param dispatch - Redux Dispatch-Funktion
 * @param title - Optionaler Titel für die Benachrichtigung
 */
export const showInfoNotification = (
    message: string,
    dispatch: Dispatch,
    title?: string
) => {
    dispatch(triggerNotification({
        title: title,
        content: message,
        type: "info",
        autoHideDuration: 3000
    }));
};

/**
 * Hilfsfunktion zum Anzeigen von Warnbenachrichtigungen
 * @param message - Die anzuzeigende Warnmeldung
 * @param dispatch - Redux Dispatch-Funktion
 * @param title - Optionaler Titel für die Benachrichtigung
 */
export const showWarningNotification = (
    message: string,
    dispatch: Dispatch,
    title?: string
) => {
    dispatch(triggerNotification({
        title: title || "Warnung",
        content: message,
        type: "warning",
        autoHideDuration: 4000
    }));
}; 